import { createContext, useContext } from "react";
import type { DocumentFolder } from "@/hooks/use-document-folders";
import type { Document } from "@/api/generated/client/models/Document";

/** Subset of Document fields needed by the folder tree UI */
export type FolderDocument = Pick<Document, "id" | "filename" | "mimeType" | "sizeBytes" | "folderId" | "createdAt">;

export interface FolderTreeContextValue {
  canWrite: boolean;

  // Data
  allFolders: DocumentFolder[];
  allDocuments: FolderDocument[];

  // Pre-computed lookup maps (O(1) per node instead of O(n) filter)
  childFoldersByParent: Map<number | null, DocumentFolder[]>;
  docsByFolder: Map<number | null, FolderDocument[]>;

  // Tree UI state
  expandedIds: Set<number>;
  toggleExpanded: (id: number) => void;

  // Rename
  renamingId: number | null;
  renameValue: string;
  setRenameValue: (v: string) => void;
  onRenameSubmit: () => void;
  onRenameCancel: () => void;
  onStartRename: (id: number, name: string) => void;

  // Create subfolder
  creatingInParent: number | null | "root";
  newName: string;
  setNewName: (v: string) => void;
  onCreateSubmit: (parentId: number | null) => void;
  onCreateCancel: () => void;
  onStartCreate: (parentId: number) => void;

  // Folder actions
  onDeleteFolder: (id: number, name: string) => void;
  isCreatingFolder: boolean;

  // Document actions
  onOpenDoc: (doc: FolderDocument) => void;
  onDownload: (docId: number, filename: string) => void;
  onDeleteDoc: (docId: number) => void;
  isDeleting: boolean;

  // Drag & drop
  draggingDocId: number | null;
  draggingFolderId: number | null;
  dropTargetId: number | null | "root";
  handleDragStart: (docId: number) => (e: React.DragEvent) => void;
  handleFolderDragStart: (folderId: number) => (e: React.DragEvent) => void;
  handleDragEnd: () => void;
  handleDrop: (folderId: number | null) => (e: React.DragEvent) => void;
  onDragOver: (id: number | "root") => (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;

  // Upload to folder
  onUploadToFolder: (folderId: number) => void;
}

export const FolderTreeContext = createContext<FolderTreeContextValue | null>(null);

export function useFolderTreeContext(): FolderTreeContextValue {
  const ctx = useContext(FolderTreeContext);
  if (!ctx) throw new Error("useFolderTreeContext must be used within FolderTreeContext.Provider");
  return ctx;
}
